import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";

type CategoryDropdownInputProps = {
  options: any[];
  name: string;
  focusName: string;
  setFocusName: (name: string) => void;
};

const CategoryDropdownInput = ({
  options = [],
  name,
  focusName,
  setFocusName,
}: CategoryDropdownInputProps) => {
  const lang = window.localStorage.getItem("language");
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("선택");
  const { setValue, watch } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null); // 버튼 위치 참조
  const selectBoxRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const theme = useTheme();

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (options.length === 0) return;

    if (focusName !== name) {
      setFocusName(name);
    } else {
      setFocusName("");
    }
    setOpen((prev) => !prev);
  };

  const handleChange = (
    value: string,
    code: number | null,
    typeId: number | null,
  ) => {
    let currentSizeTypeId = typeId;

    if (name === "depth2" && code === null) {
      // depth2에서 "선택안함" 선택 시 depth1의 size_type_id 유지
      currentSizeTypeId = watch("depth1_size_type_id");
    }

    setOpen(false);
    setSelectedValue(value);
    setValue("size_type_id", currentSizeTypeId); // Size type id 저장 (후에 sizeList 불러올때 사용)

    setFocusName(""); // 선택하면 닫기

    if (name === "depth1") {
      setValue(name, code);
      setValue("depth1_size_type_id", typeId);
      setValue("depth2", null);
      setValue("depth2_size_type_id", null);
    }
    if (name === "depth2") {
      setValue(name, code);
      setValue("depth2_size_type_id", typeId);
    }
  };

  //하나의 dropdown 만 열릴수있게
  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName, name]);

  // 최종 사이즈타입
  const watchedName = watch(name);
  useEffect(() => {
    if (watchedName === null) {
      setSelectedValue("선택");
    } else {
      const selectedOption = options.find(
        (option) => option.code === watchedName,
      );
      if (selectedOption) {
        setSelectedValue(selectedOption.name_ko);
        // 여기서 size_type_id도 설정
        if (selectedOption.size_type_id) {
          setValue("size_type_id", selectedOption.size_type_id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedName, options]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        selectBoxRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const updateDropdownPosition = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={selectedValue}
        ref={buttonRef}>
        {selectedValue}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>

      {open &&
        ReactDOM.createPortal(
          <SelectBox
            ref={selectBoxRef}
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            {name === "depth2" && (
              <OptionItem
                type="button"
                key="none"
                last={options.length === 0}
                onClick={() => handleChange("선택안함", null, null)}>
                {lang === "en" ? "None" : "선택안함"}
              </OptionItem>
            )}

            {options.map((item, index) => (
              <OptionItem
                type="button"
                key={item.code}
                last={index === options.length - 1}
                onClick={() =>
                  handleChange(
                    lang === "en" && item.name_en ? item.name_en : item.name_ko,
                    item.code,
                    item.size_type_id,
                  )
                }>
                {lang === "en" && item.name_en ? item.name_en : item.name_ko}
              </OptionItem>
            ))}
          </SelectBox>,
          document.body,
        )}
    </Container>
  );
};

export default CategoryDropdownInput;

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{
  top: number;
  left: number;
  width: number;
}>(({ theme, top, left, width }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "fixed",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  width: "100%",
  padding: "10px 30px 10px 20px",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.regular,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));
